import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, TrendingUp, Users, Receipt, Wallet, BarChart3, Download, X } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n, c = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n || 0);
const fmt2 = (n, c = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency: c }).format(n || 0);
const monthLabel = (mk) => { const [y, m] = mk.split('-'); return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short' }); };

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#94A3B8] text-xs uppercase font-bold mb-2">
        <Icon size={14} className={accent} />{label}
      </div>
      <div className="text-2xl font-bold text-[#0A0F1C]">{value}</div>
    </div>
  );
}

export default function AdminEarnings() {
  const [data, setData] = useState(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    setData(null);
    axios.get(`${API_URL}/api/admin/earnings`, { params, withCredentials: true })
      .then(r => setData(r.data)).catch(() => setData(false));
  }, [start, end]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await axios.get(`${API_URL}/api/admin/earnings/export.csv`, { params, withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `dca_earnings_${start || 'all'}_to_${end || 'now'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const clearRange = () => { setStart(''); setEnd(''); };

  if (data === null) return <AdminLayout title="Earnings"><div className="p-8 text-center text-[#94A3B8]">Loading…</div></AdminLayout>;
  if (data === false) return <AdminLayout title="Earnings"><div className="p-8 text-center text-red-600">Failed to load earnings.</div></AdminLayout>;

  const cur = data.currency || 'USD';
  const ps = data.platform_sales;
  const cf = data.connect_fees;
  const maxMonthly = Math.max(1, ...ps.monthly.map(m => m.amount));
  const maxSource = Math.max(1, ...ps.by_source.map(s => s.amount));

  return (
    <AdminLayout title="Earnings">
      <div data-testid="admin-earnings-page" className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="heading-2 text-2xl text-[#0A0F1C]">Platform Earnings</h1>
            <p className="text-sm text-[#475569]">Your own sales plus the platform fees you earn from coach transactions.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2" data-testid="earnings-toolbar">
            <div>
              <label className="block text-[11px] font-semibold text-[#94A3B8] uppercase mb-1">From</label>
              <input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm" data-testid="earnings-start" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#94A3B8] uppercase mb-1">To</label>
              <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm" data-testid="earnings-end" />
            </div>
            {(start || end) && (
              <button onClick={clearRange} className="inline-flex items-center gap-1 px-2.5 py-2 text-sm text-[#475569] hover:text-[#0A0F1C]" data-testid="earnings-clear-range"><X size={14} /> Clear</button>
            )}
            <button onClick={exportCsv} disabled={exporting} className="inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60" data-testid="earnings-export-csv">
              <Download size={15} /> {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>
        {(start || end) && (
          <p className="text-xs text-[#94A3B8]" data-testid="earnings-range-note">
            Showing total, by-source, transaction count &amp; recent list for {start || 'the beginning'} → {end || 'now'}. The 6-month trend always shows full history.
          </p>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={DollarSign} label="Total earnings" value={fmt(data.total_platform_earnings, cur)} accent="text-[#1D4ED8]" testid="kpi-total" />
          <Kpi icon={TrendingUp} label="This month" value={fmt(ps.revenue_month, cur)} accent="text-green-600" testid="kpi-month" />
          <Kpi icon={Wallet} label="Connect fees earned" value={fmt(cf.platform_fees_total, cur)} accent="text-[#7C3AED]" testid="kpi-fees" />
          <Kpi icon={Receipt} label="Transactions" value={ps.transaction_count} accent="text-[#F97316]" testid="kpi-txns" />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* Monthly trend */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="earnings-monthly">
            <h2 className="font-bold text-[#0A0F1C] mb-4 flex items-center gap-2"><BarChart3 size={16} className="text-[#1D4ED8]" />Revenue — last 6 months</h2>
            <div className="flex items-end gap-3 h-40">
              {ps.monthly.map((m) => (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full" title={fmt2(m.amount, cur)}>
                  <div className="text-[10px] text-[#475569] mb-1">{m.amount > 0 ? fmt(m.amount, cur) : ''}</div>
                  <div className="w-full bg-[#1D4ED8] rounded-t transition-all" style={{ height: `${Math.max(2, (m.amount / maxMonthly) * 100)}%` }} />
                  <div className="text-[11px] text-[#94A3B8] mt-1">{monthLabel(m.month)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* By source */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="earnings-by-source">
            <h2 className="font-bold text-[#0A0F1C] mb-4">Revenue by source</h2>
            {ps.by_source.length === 0 ? <p className="text-sm text-[#94A3B8]">No paid transactions yet.</p> : (
              <div className="space-y-3">
                {ps.by_source.map((s) => (
                  <div key={s.source}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize text-[#475569]">{s.source.replace(/_/g, ' ')} <span className="text-[#94A3B8]">({s.count})</span></span>
                      <span className="font-bold text-[#0A0F1C]">{fmt2(s.amount, cur)}</span>
                    </div>
                    <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden"><div className="h-full bg-[#F97316]" style={{ width: `${(s.amount / maxSource) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top-earning coaches */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="earnings-top-coaches">
          <h2 className="font-bold text-[#0A0F1C] mb-1 flex items-center gap-2"><Users size={16} className="text-[#7C3AED]" />Top-earning coaches</h2>
          <p className="text-xs text-[#94A3B8] mb-3">By platform fees you've collected from their sales (live from Stripe Connect).</p>
          {!cf.available ? (
            <p className="text-sm text-[#94A3B8]">Connect fee data isn't available yet (no coach sales, or Connect not configured).</p>
          ) : cf.top_coaches.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">No coach sales yet — fees will appear here as coaches make sales.</p>
          ) : (
            <div className="divide-y">
              {cf.top_coaches.map((c, i) => (
                <div key={c.account_id || i} className="flex items-center justify-between py-2.5 text-sm" data-testid={`top-coach-row-${i}`}>
                  <div><span className="font-semibold text-[#0A0F1C]">{c.coach}</span> <span className="text-xs text-[#94A3B8]">· {c.count} sale{c.count === 1 ? '' : 's'}</span></div>
                  <div className="text-right">
                    <div className="font-bold text-[#7C3AED]">{fmt2(c.fees, cur)} <span className="text-[10px] text-[#94A3B8] font-normal">fees</span></div>
                    {c.gross_estimate != null && <div className="text-[11px] text-[#94A3B8]">~{fmt2(c.gross_estimate, cur)} gross</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent transactions */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="earnings-recent">
          <h2 className="font-bold text-[#0A0F1C] mb-3">Recent transactions</h2>
          {ps.recent.length === 0 ? <p className="text-sm text-[#94A3B8]">No transactions yet.</p> : (
            <div className="divide-y">
              {ps.recent.map((t, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 text-sm" data-testid={`recent-txn-${i}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0A0F1C] truncate">{t.customer}</div>
                    <div className="text-xs text-[#94A3B8] capitalize">{(t.source || '').replace(/_/g, ' ')} · {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</div>
                  </div>
                  <div className="font-bold text-[#0A0F1C]">{fmt2(t.amount, t.currency)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
